// DialogDocumentPicker — Pick existing project documents or upload new ones

// Used in condition requirement fields to allow users to:
// 1. Select already-uploaded project documents (search + filter)
// 2. Upload a new document inline
// Supports single-select (maxSelections=1) or multi-select modes.

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
	alpha,
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	IconButton,
	InputAdornment,
	LinearProgress,
	Radio,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import {
	CheckCircle as IconSelected,
	Close as IconClose,
	CloudUpload as IconUpload,
	InsertDriveFile as IconFile,
	LibraryAdd as IconPicker,
	Search as IconSearch,
} from '@mui/icons-material';

import { ContextUser } from '../../../App.context';
import { fetchAiDocuments, uploadAiDocument } from '../../../services/aiApi';
import type { AiDocument } from '../../pages/ProjectV2/Documents/documentTypes';
import { radius } from '../../../utils/designSystem';
import { Empty } from '../../ui/Empty/Empty';

// Types

export interface DialogDocumentPickerProps {
	isOpen: boolean;
	onClose: () => void;
	/** Called with the selected/uploaded documents when user confirms */
	onConfirm: (docs: PickedDocument[]) => void;
	/** Project ID for scoping document list */
	projectId?: number;
	/** Contract ID (public.contract.id) for filtering documents to this contract */
	contractId?: number;
	/** Maximum documents that can be selected. 1 = single-select (radio), >1 or 0 = multi-select (checkbox). Default: 1 */
	maxSelections?: number;
	/** Document IDs already selected (pre-checked) */
	currentSelections?: string[];
	/** Title for the dialog */
	title?: string;
	/** Source type for new uploads */
	uploadSourceType?: string;
	/** Source id for new uploads */
	uploadSourceId?: number;
}

export interface PickedDocument {
	id: string;
	filename: string;
	doc_type?: string;
	job_id?: string;
}

// Helpers

const formatFileSize = (bytes: number | string | undefined): string => {
	const n = Number(bytes);
	if (!n || n <= 0) return '';
	const units = ['B', 'KB', 'MB', 'GB'];
	const exp = Math.floor(Math.log(n) / Math.log(1024));
	return `${parseFloat((n / Math.pow(1024, exp)).toFixed(1))} ${units[exp]}`;
};

// Component

export const DialogDocumentPicker = ({
	isOpen,
	onClose,
	onConfirm,
	projectId,
	contractId,
	maxSelections = 1,
	currentSelections = [],
	title = 'Select Documents',
	uploadSourceType,
	uploadSourceId,
}: DialogDocumentPickerProps) => {
	const theme = useTheme();
	const { user } = useContext(ContextUser);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const isSingleSelect = maxSelections === 1;

	const [documents, setDocuments] = useState<AiDocument[]>([]);
	const [loading, setLoading] = useState(false);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(currentSelections));
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);

	// Seed selection from currentSelections only when the dialog transitions to
	// open — NOT on every parent re-render. `currentSelections` is usually a
	// fresh array reference each render, and re-seeding on that identity change
	// would wipe the user's in-progress picks before they can confirm.
	const wasOpen = useRef(false);
	useEffect(() => {
		if (isOpen && !wasOpen.current) {
			setSelectedIds(new Set(currentSelections));
			setSearch('');
		}
		wasOpen.current = isOpen;
	}, [isOpen, currentSelections]);

	// Fetch project documents
	const fetchDocs = useCallback(async () => {
		if (!projectId) return;
		setLoading(true);
		setFetchError(null);
		try {
			const response = await fetchAiDocuments({
				project_id: projectId,
				...(contractId ? { tags: `contract:${contractId}` } : {}),
				populate: 'uploaded_by',
				page_size: 100,
				order_by: 'uploaded_at',
				order: 'DESC',
				...(search ? { search } : {}),
			});
			setDocuments(response.data || []);
		} catch (err) {
			console.error('Failed to fetch documents for picker:', err);
			setFetchError(err instanceof Error ? err.message : 'Failed to load documents');
		} finally {
			setLoading(false);
		}
	}, [projectId, contractId, search]);

	useEffect(() => {
		if (isOpen) fetchDocs();
	}, [isOpen, fetchDocs]);

	// Toggle selection
	const handleToggle = useCallback(
		(docId: string) => {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				if (next.has(docId)) {
					next.delete(docId);
				} else {
					if (isSingleSelect) {
						// Single-select: replace
						next.clear();
					} else if (maxSelections > 0 && next.size >= maxSelections) {
						// At limit — don't add
						return prev;
					}
					next.add(docId);
				}
				return next;
			});
		},
		[isSingleSelect, maxSelections]
	);

	// Upload new document
	const handleFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files || []);
			if (files.length === 0 || !user?.id) return;

			setUploading(true);
			setUploadProgress(0);
			try {
				// Build tags so the upload matches the picker's list filter (which filters by
				// `contract:${contractId}` when a contract is in scope). Without the contract tag,
				// the freshly-uploaded doc would not appear in the refreshed list.
				const tagParts: string[] = [];
				if (uploadSourceType && uploadSourceId) {
					tagParts.push(`${uploadSourceType}:${uploadSourceId}`);
				}
				if (contractId) {
					tagParts.push(`contract:${contractId}`);
				}
				const uploadTag = tagParts.length > 0 ? tagParts.join(',') : undefined;

				const newIds: string[] = [];
				for (let i = 0; i < files.length; i++) {
					const result = await uploadAiDocument({
						file: files[i],
						project_id: projectId,
						source_type: uploadSourceType,
						source_id: uploadSourceId,
						tag: uploadTag,
					});
					newIds.push(result.document_id);
					setUploadProgress(Math.round(((i + 1) / files.length) * 100));
				}
				// Refresh doc list and auto-select new uploads
				await fetchDocs();
				setSelectedIds((prev) => {
					const next = new Set(isSingleSelect ? [] : Array.from(prev));
					newIds.forEach((id) => {
						if (maxSelections === 0 || next.size < maxSelections || isSingleSelect) {
							if (isSingleSelect) next.clear();
							next.add(id);
						}
					});
					return next;
				});
			} catch (err) {
				console.error('Upload failed:', err);
			} finally {
				setUploading(false);
				setUploadProgress(0);
				if (fileInputRef.current) fileInputRef.current.value = '';
			}
		},
		[
			user,
			projectId,
			contractId,
			uploadSourceType,
			uploadSourceId,
			fetchDocs,
			isSingleSelect,
			maxSelections,
		]
	);

	// Confirm selection
	const handleConfirm = useCallback(() => {
		const picked: PickedDocument[] = documents
			.filter((d) => selectedIds.has(d.id))
			.map((d) => ({
				id: d.id,
				filename: d.filename,
				doc_type: d.doc_type || undefined,
				job_id: d.job_id || undefined,
			}));
		onConfirm(picked);
		onClose();
	}, [documents, selectedIds, onConfirm, onClose]);

	const limitLabel =
		maxSelections === 0
			? `${selectedIds.size} selected`
			: `${selectedIds.size} of ${maxSelections}`;

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			PaperProps={{ sx: { borderRadius: radius.lg, maxHeight: '80vh' } }}>
			{/* Header */}
			<Box
				sx={{
					px: 3,
					py: 2,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 36, height: 36 }}>
						<IconPicker sx={{ color: 'primary.main', fontSize: 20 }} />
					</Avatar>
					<Typography variant="h6" fontWeight={700}>
						{title}
					</Typography>
					<Chip label={limitLabel} size="small" sx={{ fontWeight: 600 }} />
				</Stack>
				<IconButton onClick={onClose} size="small">
					<IconClose />
				</IconButton>
			</Box>

			{/* Search + Upload */}
			<Box sx={{ px: 3, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<TextField
						size="small"
						placeholder="Search documents..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<IconSearch sx={{ fontSize: 18, color: 'text.secondary' }} />
								</InputAdornment>
							),
						}}
						sx={{ flex: 1 }}
					/>
					<input ref={fileInputRef} type="file" multiple hidden onChange={handleFileChange} />
					<Button
						variant="outlined"
						size="small"
						startIcon={<IconUpload />}
						disabled={uploading}
						onClick={() => fileInputRef.current?.click()}
						sx={{
							borderRadius: radius.md,
							textTransform: 'none',
							fontWeight: 600,
							whiteSpace: 'nowrap',
						}}>
						Upload New
					</Button>
				</Stack>
			</Box>

			{/* Upload progress */}
			{uploading && (
				<LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 3 }} />
			)}

			{/* Document List */}
			<DialogContent sx={{ p: 0 }}>
				{loading ? (
					<Box sx={{ py: 4 }}>
						<Empty
							isLoading
							IconCircleProps={{ type: 'document', size: 'md' }}
							title={{ children: '' }}
							content={{ children: 'Loading documents…' }}
						/>
					</Box>
				) : fetchError ? (
					<Box sx={{ py: 4 }}>
						<Empty
							IconCircleProps={{ type: 'document', size: 'md' }}
							title={{ children: 'Unable to load documents' }}
							content={{ children: fetchError }}
							actions={[
								{
									children: 'Retry',
									variant: 'contained',
									onClick: fetchDocs,
								},
							]}
						/>
					</Box>
				) : documents.length === 0 ? (
					<Box sx={{ py: 4 }}>
						<Empty
							IconCircleProps={{ type: 'document', size: 'md' }}
							title={{ children: search ? 'No documents match your search' : 'No documents yet' }}
							content={{
								children: search
									? 'Try a different search term.'
									: 'Upload a document to get started.',
							}}
							actions={
								search
									? []
									: [
											{
												children: 'Upload New',
												variant: 'contained',
												startIcon: <IconUpload />,
												onClick: () => fileInputRef.current?.click(),
											},
										]
							}
						/>
					</Box>
				) : (
					<Box>
						{documents.map((doc, idx) => {
							const isSelected = selectedIds.has(doc.id);
							const metadata = (doc.metadata || {}) as Record<string, unknown>;
							const sizeStr = formatFileSize(
								metadata.file_size_bytes as string | number | undefined
							);
							const atLimit = maxSelections > 0 && selectedIds.size >= maxSelections && !isSelected;

							return (
								<Stack
									key={doc.id || idx}
									direction="row"
									alignItems="center"
									spacing={1.5}
									onClick={() => !atLimit && handleToggle(doc.id)}
									sx={{
										px: 2.5,
										py: 1,
										cursor: atLimit ? 'default' : 'pointer',
										opacity: atLimit ? 0.5 : 1,
										bgcolor: isSelected
											? alpha(theme.palette.primary.main, 0.06)
											: idx % 2 === 0
												? 'transparent'
												: 'action.hover',
										'&:hover': atLimit ? {} : { bgcolor: alpha(theme.palette.primary.main, 0.08) },
									}}>
									{/* Selection control */}
									{isSingleSelect ? (
										<Radio checked={isSelected} size="small" sx={{ p: 0.5 }} />
									) : (
										<Checkbox
											checked={isSelected}
											size="small"
											sx={{ p: 0.5 }}
											disabled={atLimit}
										/>
									)}

									{/* File icon */}
									<Avatar
										sx={{
											bgcolor: alpha(theme.palette.primary.main, 0.08),
											width: 32,
											height: 32,
											flexShrink: 0,
										}}>
										<IconFile sx={{ color: 'primary.main', fontSize: 16 }} />
									</Avatar>

									{/* Doc info */}
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography
											sx={{ fontWeight: 600, fontSize: '1.25rem' }}
											noWrap
											title={doc.filename}>
											{doc.filename}
										</Typography>
										<Stack direction="row" spacing={1} alignItems="center">
											{doc.doc_type && (
												<Chip
													label={doc.doc_type}
													size="small"
													sx={{ height: 18, fontSize: '1rem' }}
												/>
											)}
											{sizeStr && (
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ fontSize: '1rem' }}>
													{sizeStr}
												</Typography>
											)}
										</Stack>
									</Box>

									{/* Selected indicator */}
									{isSelected && <IconSelected sx={{ color: 'primary.main', fontSize: 20 }} />}
								</Stack>
							);
						})}
					</Box>
				)}
			</DialogContent>

			{/* Footer */}
			<DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
				<Button onClick={onClose} sx={{ textTransform: 'none' }}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleConfirm}
					disabled={selectedIds.size === 0}
					sx={{ textTransform: 'none', fontWeight: 600, borderRadius: radius.md }}>
					{isSingleSelect
						? 'Select Document'
						: `Select ${selectedIds.size} Document${selectedIds.size !== 1 ? 's' : ''}`}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
